// Platform Icons
import AzureIcon from 'components/icons/AzureIcon';
import CiscoIcon from 'components/icons/CiscoIcon';
import EightByEightIcon from 'components/icons/EightByEightIcon';
import SBCIcon from 'components/icons/SBCIcon';
import TeamsIcon from 'components/icons/TeamsIcon';
import ZoomIcon from 'components/icons/ZoomIcon';
import GCCHighIcon from 'components/icons/GCCHighIcon';
import RingCentralIcon from 'components/icons/RingCentralIcon';
import DialPadIcon from 'components/icons/DialPadIcon';
import OperatorPlatformIcon from 'components/icons/OperatorPlatformIcon';

import { PlatformEnums } from '@/utils/platforms';

// Helper function to get platform configuration by ID
const getPlatformConfig = (platformID) => {
    const platforms = {
        [PlatformEnums.AZURE]: {
            name: 'Azure Communication Services',
            icon: AzureIcon,
        },
        [PlatformEnums.WEBEX]: {
            name: 'Cisco Webex',
            icon: CiscoIcon,
        },
        [PlatformEnums.ZOOM]: {
            name: 'Zoom',
            icon: ZoomIcon,
        },
        [PlatformEnums.SBC]: {
            name: 'SBCaaS',
            icon: SBCIcon,
        },
        [PlatformEnums.TEAMS]: {
            name: 'Microsoft Teams',
            icon: TeamsIcon,
        },
        [PlatformEnums.EIGHTBYEIGHT]: {
            name: '8x8',
            icon: EightByEightIcon,
        },
        [PlatformEnums.GCC]: {
            name: 'GCC',
            icon: GCCHighIcon,
        },
        [PlatformEnums.RINGCENTRAL]: {
            name: 'RingCentral',
            icon: RingCentralIcon,
        },
        [PlatformEnums.DIALPAD]: {
            name: 'DialPad',
            icon: DialPadIcon,
        },

        [PlatformEnums.OPERATOR_PLATFORM]: {
            name: 'Operator Platform',
            icon: OperatorPlatformIcon,
        },
    };
    return platforms[platformID] || { name: 'Unknown Platform', icon: null };
};

// draggableStyles
const grid = 8;
const getItemStyle = (isDragging, draggableStyle) => {
    return {
        // our custom properties will override draggableStyle
        opacity: isDragging ? 0.9 : 1,
        transform: isDragging
            ? `${draggableStyle?.transform} rotate(0deg) scale(1.01)`
            : 'none',
        transition: isDragging ? 'none' : 'all 0.2s ease',
        zIndex: isDragging ? 1000 : 'auto',
        // styles we need to apply on draggables first
        ...draggableStyle,
    };
};
const getListStyle = (isDraggingOver, darkMode) => ({
    backgroundColor: isDraggingOver
        ? darkMode
            ? 'inherit'
            : '#f7fafc'
        : 'transparent',
    borderRadius: isDraggingOver ? '8px' : '0px',
    padding: grid,
    transition: 'all 0.2s ease',
    minHeight: '100px',
    border: isDraggingOver
        ? '2px dashed var(--chakra-colors-brand-400)'
        : 'none',
});

const getCloneItemStyle = (isDragging, draggableStyle, isDropAnimating) => {
    if (isDropAnimating) {
        // When dropping to a different container (like a group), the transform will be
        // undefined or 'translate(0px, 0px)'. In this case, hide the clone to prevent
        // the visual artifact of the clone appearing at top-left corner.
        const transform = draggableStyle?.transform;
        if (!transform || transform === 'translate(0px, 0px)') {
            return {
                ...draggableStyle,
                opacity: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
            };
        }

        return {
            ...draggableStyle,
            // Ensure smooth transition back to original position
            transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
            // Maintain visibility during animation
            opacity: 0.8,
            transform: transform,
        };
    }

    return {
        ...draggableStyle,
        opacity: isDragging ? 0.9 : 1,
        transform: isDragging
            ? `${draggableStyle?.transform} rotate(0deg) scale(1.01)`
            : 'none',
        transition: isDragging ? 'none' : 'all 0.2s ease',
    };
};

// Styles for group drop zone
const getDropZoneStyle = (isDraggingOver, darkMode) => ({
    border: '2px dashed',
    borderColor: isDraggingOver
        ? 'var(--chakra-colors-blue-400)'
        : 'var(--chakra-colors-gray-400)',
    borderRadius: '8px',
    backgroundColor: isDraggingOver
        ? darkMode
            ? 'var(--chakra-colors-blue-900)'
            : 'var(--chakra-colors-blue-50)'
        : 'transparent',
    minHeight: '80px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    transition: 'all 0.2s ease',
});

// Styles for group accordion items
const getGroupStyle = (isDragging, draggableStyle) => ({
    ...draggableStyle,
    opacity: isDragging ? 0.95 : 1,
    transform: isDragging
        ? `${draggableStyle?.transform} rotate(0deg) scale(1.02)`
        : 'none',
    transition: isDragging ? 'none' : 'all 0.2s ease',
    zIndex: isDragging ? 1001 : 'auto',
});

export {
    getPlatformConfig,
    getItemStyle,
    getListStyle,
    getCloneItemStyle,
    getDropZoneStyle,
    getGroupStyle,
};
